// src/pages/customer-panel/personalize/PersonalizeDocumentsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, CircularProgress, Alert, Snackbar, Tooltip, Checkbox,
    TextField, InputAdornment, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    LinearProgress, FormControl, InputLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

// ── Processing Dialog ──
function ProcessingDialog({ open, items, action, onClose, title }) {
    const [current, setCurrent] = useState(0);
    const [done, setDone] = useState(false);
    const [results, setResults] = useState([]);

    useEffect(() => {
        if (!open || !items.length) return;
        setCurrent(0); setDone(false); setResults([]);
        let cancelled = false;
        (async () => {
            const res = [];
            for (let i = 0; i < items.length; i++) {
                if (cancelled) break;
                try { await action(items[i].id); res.push({ id: items[i].id, ok: true }); }
                catch { res.push({ id: items[i].id, ok: false }); }
                setCurrent(i + 1); setResults([...res]);
            }
            setDone(true);
        })();
        return () => { cancelled = true; };
    }, [open, items, action]);

    if (!open) return null;
    const progress = items.length ? Math.round((current / items.length) * 100) : 0;
    return (
        <Dialog open maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, mb: 2, color: '#64748b' }}>
                    Processing {current} of {items.length}...
                </Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: `${GREEN_ACCENT}20`, '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT } }} />
                {done && <Alert severity="success" sx={{ mt: 2, fontFamily, borderRadius: '10px' }}>All items processed!</Alert>}
            </DialogContent>
            {done && (
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={onClose} variant="contained" sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>Done</Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

// ── Edit File Content Dialog ──
function EditFileDialog({ open, fileId, onClose, onSaved }) {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !fileId) return;
        setLoading(true);
        axiosInstance.get(`/employee-drive/files/${fileId}/content`)
            .then(r => setContent(r.data.page_content || r.data.content || ''))
            .catch(() => setContent(''))
            .finally(() => setLoading(false));
    }, [open, fileId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put(`/employee-drive/files/${fileId}/content`, { page_content: content });
            onSaved();
        } catch { /* snackbar handled by parent */ }
        setSaving(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Edit Document Content</DialogTitle>
            <DialogContent>
                {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box> : (
                    <TextField fullWidth multiline minRows={12} maxRows={20} value={content} onChange={e => setContent(e.target.value)}
                        sx={{ mt: 1, fontFamily, '& .MuiInputBase-root': { fontFamily, fontSize: '0.85rem' } }} />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} variant="contained"
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Import from Library Dialog ──
function ImportLibraryDialog({ open, onClose, onSuccess }) {
    const [docs, setDocs] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/employee-drive/available-documents', { params: { page, limit, search } });
            setDocs(data.documents || []); setTotal(data.total || 0);
        } catch { /* ignore */ }
        setLoading(false);
    }, [page, search]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const toggle = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleAll = () => setSelected(prev => prev.size === docs.length ? new Set() : new Set(docs.map(d => d.id)));

    const handleImport = async () => {
        setImporting(true);
        try {
            await axiosInstance.post('/employee-drive/import-documents', { document_ids: Array.from(selected) });
            onSuccess(); onClose();
        } catch { /* ignore */ }
        setImporting(false);
    };

    const formatDate = d => { try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Import Documents from Library</DialogTitle>
            <DialogContent>
                <TextField fullWidth size="small" placeholder="Search documents..." value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                    sx={{ mb: 2, mt: 1, fontFamily }} />
                {docs.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Checkbox size="small" checked={selected.size === docs.length && docs.length > 0} onChange={toggleAll} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>Select All ({selected.size} of {docs.length})</Typography>
                    </Box>
                )}
                <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                    {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                    : docs.length === 0 ? <Typography sx={{ fontFamily, textAlign: 'center', py: 4, color: '#94a3b8' }}>{search ? 'No documents found' : 'No documents available'}</Typography>
                    : docs.map(doc => (
                        <Box key={doc.id} onClick={() => toggle(doc.id)}
                            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: '10px', cursor: 'pointer', border: '1px solid', borderColor: selected.has(doc.id) ? GREEN_ACCENT : 'grey.200', bgcolor: selected.has(doc.id) ? `${GREEN_ACCENT}08` : 'transparent', mb: 1, '&:hover': { borderColor: GREEN_ACCENT } }}>
                            <Checkbox size="small" checked={selected.has(doc.id)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <DescriptionIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.filename}</Typography>
                                </Box>
                                {doc.summary && <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{doc.summary}</Typography>}
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                    {doc.page_words} words &bull; {doc.tokens} tokens{doc.upload_date ? ` \u2022 ${formatDate(doc.upload_date)}` : ''}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                {total > limit && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'grey.200' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                            {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)} sx={{ fontFamily, textTransform: 'none', fontSize: '0.75rem' }}>Previous</Button>
                            <Button size="small" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)} sx={{ fontFamily, textTransform: 'none', fontSize: '0.75rem' }}>Next</Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={importing} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleImport} disabled={importing || selected.size === 0} variant="contained"
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    {importing ? 'Importing...' : `Import${selected.size > 0 ? ` (${selected.size})` : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Import from Drive Dialog ──
function ImportFromDriveDialog({ open, onClose, onSuccess }) {
    const [files, setFiles] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [search, setSearch] = useState('');
    const [pageToken, setPageToken] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const load = useCallback(async (searchVal, token) => {
        setLoading(true);
        try {
            const params = {};
            if (searchVal) params.search = searchVal;
            if (token) params.page_token = token;
            const { data } = await axiosInstance.get('/employee-drive/browse-files', { params });
            if (token) { setFiles(prev => [...prev, ...(data.files || [])]); }
            else { setFiles(data.files || []); }
            setHasMore(!!data.next_page_token);
            setPageToken(data.next_page_token || null);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { if (open) load(); }, [open]);

    const handleSearch = () => { setSelected(new Set()); setPageToken(null); load(search); };
    const toggle = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleAll = () => setSelected(prev => prev.size === files.length ? new Set() : new Set(files.map(f => f.drive_file_id)));

    const handleImport = async () => {
        setImporting(true);
        try {
            await axiosInstance.post('/employee-drive/import-from-drive', { file_ids: Array.from(selected) });
            onSuccess(); onClose();
        } catch { /* ignore */ }
        setImporting(false);
    };

    const fmtSize = b => { if (!b) return '--'; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`; return `${(b / 1048576).toFixed(1)} MB`; };
    const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Import from Your Drive</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                    <TextField fullWidth size="small" placeholder="Search files in your drive..." value={search}
                        onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                        sx={{ fontFamily }} />
                    <IconButton onClick={handleSearch} disabled={loading} sx={{ color: GREEN_ACCENT }}><SearchIcon /></IconButton>
                    <IconButton onClick={() => { setSearch(''); setSelected(new Set()); setPageToken(null); load(); }} disabled={loading} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton>
                </Box>
                {files.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Checkbox size="small" checked={selected.size === files.length && files.length > 0} onChange={toggleAll} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>Select All ({selected.size} of {files.length})</Typography>
                    </Box>
                )}
                <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                    {loading && files.length === 0 ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                    : files.length === 0 ? <Typography sx={{ fontFamily, textAlign: 'center', py: 4, color: '#94a3b8' }}>{search ? 'No files found' : 'No files in your drive'}</Typography>
                    : <>
                        {files.map(f => (
                            <Box key={f.drive_file_id} onClick={() => toggle(f.drive_file_id)}
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: '10px', cursor: 'pointer', border: '1px solid', borderColor: selected.has(f.drive_file_id) ? GREEN_ACCENT : 'grey.200', bgcolor: selected.has(f.drive_file_id) ? `${GREEN_ACCENT}08` : 'transparent', mb: 1, '&:hover': { borderColor: GREEN_ACCENT } }}>
                                <Checkbox size="small" checked={selected.has(f.drive_file_id)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <DescriptionIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</Typography>
                                    </Box>
                                    {f.folder_path && <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}><FolderOpenIcon sx={{ fontSize: 12 }} />{f.folder_path}</Typography>}
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                        {fmtSize(f.size)}{f.created_time ? ` \u2022 ${fmtDate(f.created_time)}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        {hasMore && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button size="small" onClick={() => load(search, pageToken)} disabled={loading} sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT }}>{loading ? 'Loading...' : 'Load More'}</Button>
                            </Box>
                        )}
                    </>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={importing} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleImport} disabled={importing || selected.size === 0} variant="contained"
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    {importing ? 'Importing...' : `Import${selected.size > 0 ? ` (${selected.size})` : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main Page ──
export default function PersonalizeDocumentsPage() {
    const [driveStatus, setDriveStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [connectionError, setConnectionError] = useState(false);

    const [files, setFiles] = useState([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [filesLoading, setFilesLoading] = useState(false);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [uploading, setUploading] = useState(false);
    const [editFileId, setEditFileId] = useState(null);
    const [showImportLibrary, setShowImportLibrary] = useState(false);
    const [showImportDrive, setShowImportDrive] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [processingState, setProcessingState] = useState({ open: false, items: [], action: null, title: '' });
    const fileInputRef = useRef(null);

    const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    // Load drive status
    useEffect(() => {
        document.title = 'Personalize Documents | Customer Panel';
        (async () => {
            setStatusLoading(true);
            try {
                const { data } = await axiosInstance.get('/employee-drive/status');
                setDriveStatus(data);
            } catch { setDriveStatus(null); }
            setStatusLoading(false);
        })();
    }, []);

    // Load files
    const loadFiles = useCallback(async () => {
        if (!driveStatus?.is_connected) return;
        setFilesLoading(true);
        try {
            const { data } = await axiosInstance.get('/employee-drive/files', { params: { page, limit, search: searchTerm, status: statusFilter } });
            setFiles(data.files || []); setTotalFiles(data.total || 0);
        } catch { setFiles([]); setTotalFiles(0); }
        setFilesLoading(false);
    }, [page, limit, searchTerm, statusFilter, driveStatus]);

    useEffect(() => { loadFiles(); }, [loadFiles]);

    const handleConnect = async (driveType) => {
        try {
            const { data } = await axiosInstance.get(`/employee-drive/${driveType}/auth/url`);
            if (data.url) window.location.href = data.url;
        } catch (err) { showMsg(err.response?.data?.message || 'Failed to get auth URL', 'error'); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect your drive?')) return;
        try {
            await axiosInstance.delete('/employee-drive/disconnect');
            const { data } = await axiosInstance.get('/employee-drive/status');
            setDriveStatus(data); setConnectionError(false);
            showMsg('Drive disconnected');
        } catch (err) { showMsg(err.response?.data?.message || 'Failed to disconnect', 'error'); }
    };

    const handleUpload = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        setUploading(true);
        try {
            const fd = new FormData(); fd.append('file', file);
            await axiosInstance.post('/employee-drive/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            showMsg('File uploaded successfully!'); setConnectionError(false); loadFiles();
        } catch (err) {
            if (err.response?.data?.error_code === 'REAUTH_REQUIRED') {
                setConnectionError(true); showMsg('Drive connection expired. Please reconnect.', 'error');
                if (window.confirm('Your drive connection has expired. Reconnect now?')) handleConnect(driveStatus.drive_type);
            } else { showMsg(err.response?.data?.message || 'Upload failed', 'error'); }
        } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const handleDownload = async (fileId) => {
        try {
            const response = await axiosInstance.get(`/employee-drive/files/${fileId}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a'); a.href = url; a.setAttribute('download', 'document');
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
        } catch { showMsg('Download failed', 'error'); }
    };

    const handleBulkTrain = (action) => {
        const sel = files.filter(f => selectedIds.has(f.id));
        if (!sel.length) { showMsg('Please select at least one file.', 'error'); return; }
        setProcessingState({
            open: true, items: sel, title: action === 'train' ? 'Training Files' : 'Untraining Files',
            action: async (id) => { await axiosInstance.post('/employee-drive/files/training-status', { file_ids: [id], is_trained: action === 'train' }); },
        });
    };

    const handleBulkPublic = async (isPublic) => {
        const sel = files.filter(f => selectedIds.has(f.id));
        if (!sel.length) { showMsg('Please select at least one file.', 'error'); return; }
        if (isPublic) {
            const untrained = sel.filter(f => !f.isTrained);
            if (untrained.length) { showMsg(`${untrained.length} file(s) must be trained before marking as public.`, 'error'); return; }
        }
        if (!window.confirm(`Mark ${sel.length} file(s) as ${isPublic ? 'public' : 'private'}?`)) return;
        try {
            await axiosInstance.post('/employee-drive/files/is-public', { file_ids: sel.map(f => f.id), is_public: isPublic });
            showMsg(`Files marked as ${isPublic ? 'public' : 'private'}!`); setSelectedIds(new Set()); loadFiles();
        } catch { showMsg('Failed to update public status', 'error'); }
    };

    const closeProcessing = () => { setProcessingState({ open: false, items: [], action: null, title: '' }); setSelectedIds(new Set()); loadFiles(); };

    const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleAll = () => setSelectedIds(prev => prev.size === files.length ? new Set() : new Set(files.map(f => f.id)));

    const totalPages = Math.ceil(totalFiles / limit);
    const formatDate = (d) => { if (!d) return '--'; try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '--'; } };

    if (statusLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>Personalize Documents</Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>Upload and manage your personal documents</Typography>
            </Box>

            {!driveStatus?.is_connected ? (
                /* Connect Drive Card */
                <Paper sx={{ p: 4, borderRadius: '16px', border: '1px solid', borderColor: 'grey.100' }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, mb: 1 }}>Connect Your Personal Drive</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>Connect your Google Drive or OneDrive to upload and manage your documents.</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Button onClick={() => handleConnect('google_drive')} variant="outlined"
                            sx={{ fontFamily, fontWeight: 600, py: 3, borderRadius: '12px', textTransform: 'none', borderColor: 'grey.300', color: '#1e293b', '&:hover': { borderColor: GREEN_ACCENT, bgcolor: `${GREEN_ACCENT}08` } }}>
                            Connect Google Drive
                        </Button>
                        <Button onClick={() => handleConnect('onedrive')} variant="outlined"
                            sx={{ fontFamily, fontWeight: 600, py: 3, borderRadius: '12px', textTransform: 'none', borderColor: 'grey.300', color: '#1e293b', '&:hover': { borderColor: GREEN_ACCENT, bgcolor: `${GREEN_ACCENT}08` } }}>
                            Connect OneDrive
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <>
                    {/* Connection Error Banner */}
                    {connectionError && (
                        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: '12px', fontFamily }}
                            action={<Button color="inherit" size="small" onClick={() => handleConnect(driveStatus.drive_type)} sx={{ fontFamily, fontWeight: 600, textTransform: 'none' }}>Reconnect Now</Button>}>
                            Your drive connection has expired. Please reconnect to continue.
                        </Alert>
                    )}

                    {/* Drive Status Card */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <InsertDriveFileIcon sx={{ fontSize: 32, color: GREEN_ACCENT }} />
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>{driveStatus.details?.name || driveStatus.details?.email || 'Connected Drive'}</Typography>
                                    {driveStatus.details?.email && <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{driveStatus.details.email}</Typography>}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => handleConnect(driveStatus.drive_type)}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderColor: GREEN_ACCENT, color: GREEN_ACCENT, borderRadius: '10px', '&:hover': { borderColor: '#059669' } }}>Reconnect</Button>
                                <Button size="small" variant="outlined" onClick={handleDisconnect} startIcon={<LinkOffIcon />}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderColor: 'grey.300', color: '#64748b', borderRadius: '10px' }}>Disconnect</Button>
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', mb: 1 }}>Upload New Document</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button component="label" startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                                    disabled={uploading} variant="contained"
                                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                                    Upload File
                                    <input type="file" hidden ref={fileInputRef} onChange={handleUpload} />
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Import from Drive */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button startIcon={<FolderOpenIcon />} onClick={() => setShowImportDrive(true)} variant="outlined"
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderColor: GREEN_ACCENT, color: GREEN_ACCENT, borderRadius: '10px', '&:hover': { borderColor: '#059669', bgcolor: `${GREEN_ACCENT}08` } }}>
                            Import from Drive
                        </Button>
                    </Box>

                    {/* Files Table */}
                    <Paper sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', overflow: 'hidden' }}>
                        {/* Header + Bulk Actions */}
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>My Personal Documents</Typography>
                            {selectedIds.size > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, fontSize: '0.85rem' }}>{selectedIds.size} selected</Typography>
                                    <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => handleBulkTrain('train')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#10b981', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#059669' } }}>Trained</Button>
                                    <Button size="small" startIcon={<CancelIcon />} onClick={() => handleBulkTrain('untrain')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#f59e0b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#d97706' } }}>Untrained</Button>
                                    <Button size="small" startIcon={<PublicIcon />} onClick={() => handleBulkPublic(true)}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#3b82f6', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#2563eb' } }}>Public</Button>
                                    <Button size="small" startIcon={<VisibilityOffIcon />} onClick={() => handleBulkPublic(false)}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#64748b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#475569' } }}>Private</Button>
                                    <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT }}>Clear</Button>
                                </Box>
                            )}
                        </Box>

                        {/* Search + Filter */}
                        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <TextField size="small" placeholder="Search documents..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                                sx={{ flex: 1, minWidth: 200, fontFamily }} />
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} sx={{ fontFamily }}>
                                    <MenuItem value="all" sx={{ fontFamily }}>All</MenuItem>
                                    <MenuItem value="trained" sx={{ fontFamily }}>Trained</MenuItem>
                                    <MenuItem value="untrained" sx={{ fontFamily }}>Untrained</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Refresh"><IconButton onClick={loadFiles} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton></Tooltip>
                        </Box>

                        {/* Table */}
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.size === files.length && files.length > 0} indeterminate={selectedIds.size > 0 && selectedIds.size < files.length} onChange={toggleAll} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT }, '&.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} /></TableCell>
                                        {['Filename', 'Training', 'Visibility', 'Upload Date', 'Actions'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filesLoading ? (
                                        <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} sx={{ color: GREEN_ACCENT }} /></TableCell></TableRow>
                                    ) : files.length === 0 ? (
                                        <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <InsertDriveFileIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No documents found</Typography>
                                        </TableCell></TableRow>
                                    ) : files.map(f => (
                                        <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.has(f.id)} onChange={() => toggleSelect(f.id)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} /></TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DescriptionIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={f.isTrained ? 'Trained' : 'Untrained'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: f.isTrained ? '#d1fae5' : '#fef3c7', color: f.isTrained ? '#10b981' : '#f59e0b' }} />
                                            </TableCell>
                                            <TableCell>
                                                {f.isTrained ? (
                                                    f.isPublic ? (
                                                        <Chip icon={<PublicIcon sx={{ fontSize: 14 }} />} label="Public" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#3b82f6' }} />
                                                    ) : (
                                                        <Chip icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />} label="Private" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
                                                    )
                                                ) : <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>&mdash;</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>{formatDate(f.upload_date || f.created_at)}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditFileId(f.id)} sx={{ color: '#3b82f6', mr: 0.5 }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(f.id)} sx={{ color: '#64748b', mr: 0.5 }}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                    {(page - 1) * limit + 1}–{Math.min(page * limit, totalFiles)} of {totalFiles}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}><NavigateBeforeIcon /></IconButton>
                                    <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><NavigateNextIcon /></IconButton>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </>
            )}

            {/* Dialogs */}
            <EditFileDialog open={!!editFileId} fileId={editFileId} onClose={() => setEditFileId(null)} onSaved={() => { setEditFileId(null); showMsg('File content updated!'); loadFiles(); }} />
            <ImportLibraryDialog open={showImportLibrary} onClose={() => setShowImportLibrary(false)} onSuccess={() => { showMsg('Documents imported!'); loadFiles(); }} />
            <ImportFromDriveDialog open={showImportDrive} onClose={() => setShowImportDrive(false)} onSuccess={() => { showMsg('Files imported from drive!'); loadFiles(); }} />
            <ProcessingDialog open={processingState.open} items={processingState.items} action={processingState.action} onClose={closeProcessing} title={processingState.title} />

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
